import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ReactDOM from 'react-dom';
import { useModalFocus } from '../utils/hooks/useModalFocus';

interface WhatIsTalentfunnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToFaq: () => void;
  onNavigateToAuth: () => void;
}

const PARAGRAPH_1 = `Talentfunn er for deg som har ferdigheter, eller leter etter noen med praktisk kompetanse. Talenter kan vise frem prosjekter og tjenester, mens arbeidsgivere og andre brukere kan finne relevante profiler og ta kontakt.`;

const PARAGRAPH_2 = `Så enten du er flink på å skru på bil, har en hobby eller lidenskap du vil vise frem, vil vi gjerne at du oppretter en gratis profil på Talentfunn.no.`;

const PARAGRAPH_3 = `Ta den tiden du trenger. Profilen din er helt privat og usynlig for andre helt frem til du selv velger å publisere ditt første prosjekt. Du bytter bilder og endrer tekst i fred og ro helt til du er 100% fornøyd.`;

export function WhatIsTalentfunnModal({
  isOpen,
  onClose,
  onNavigateToFaq,
  onNavigateToAuth,
}: WhatIsTalentfunnModalProps) {
  useModalFocus(isOpen);

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div key="what-is-talentfunn-modal" className="fixed inset-0" style={{ zIndex: 'var(--z-important)' }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 pb-4 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Lukk"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-xl font-bold text-gray-900 pr-10">Hva er Talentfunn?</h2>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 space-y-4">
                <p className="text-gray-700 leading-relaxed">{PARAGRAPH_1}</p>
                <p className="text-gray-700 leading-relaxed">{PARAGRAPH_2}</p>
                <p className="text-gray-700 leading-relaxed">{PARAGRAPH_3}</p>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-2">
                  <button
                    onClick={() => {
                      onClose();
                      onNavigateToAuth();
                    }}
                    className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                  >
                    Opprett profil nå!
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      onNavigateToFaq();
                    }}
                    className="w-full px-6 py-3 text-purple-600 hover:bg-purple-50 rounded-xl font-medium transition-colors border border-purple-200"
                  >
                    Les mer i FAQ
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
